"use client";

import { motion } from "framer-motion";
import { ExternalLink, Globe2, Landmark, ShieldCheck } from "lucide-react";
import { PORTAILS, RESEAU, SIGNATURE } from "@/data/catalog";

export function PortailsView() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
      <div className="text-center mb-12">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3 flex items-center justify-center gap-2">
          <Globe2 className="h-4 w-4" /> Navigation morphing — l&rsquo;Empire rayonne
        </p>
        <h1 className="font-display text-4xl md:text-6xl font-bold text-[#F5EFE0]">
          Les <span className="gold-text">8 Portails</span> du Réseau Impérial
        </h1>
        <p className="text-[#F5EFE0]/65 max-w-2xl mx-auto mt-4">
          Huit destinations sœurs, un seul serment. Passez la main sur un bouton :
          sa forme se métamorphose — du rectangle d&rsquo;émeraude au sceau bordeaux —
          puis vous emporte vers l&rsquo;autre rive du royaume.
        </p>
        <div className="gold-line w-80 mx-auto mt-6" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PORTAILS.map((p, i) => (
          <motion.a
            key={p.url}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            className="morph-btn rounded-2xl p-5 flex flex-col items-center text-center gap-3 focus:outline-none focus:ring-2 focus:ring-[#F6E27A]/70"
            aria-label={"Visiter le portail " + p.nom + " (nouvelle fenêtre)"}
          >
            <span className="h-11 w-11 rounded-full border border-[#D4AF37]/50 bg-[#04180f]/70 flex items-center justify-center shrink-0">
              <Landmark className="h-5 w-5 text-[#F6E27A]" />
            </span>
            <span className="font-display font-bold text-[#F5EFE0] group-hover:text-[#F6E27A]">{p.nom}</span>
            <span className="text-xs text-[#F5EFE0]/60 leading-relaxed">{p.desc}</span>
            <span className="mt-auto pt-2 inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-[#F6E27A]">
              Visiter <ExternalLink className="h-3 w-3" />
            </span>
          </motion.a>
        ))}
      </div>

      {/* ─────── Réseau des sites principaux ─────── */}
      <div className="mt-16">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#F5EFE0] text-center mb-8">
          Les <span className="gold-text">5 Domaines</span> d&rsquo;Adji Anta Diop
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {RESEAU.map((s) => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="card-empire rounded-xl px-5 py-4 flex items-center justify-between gap-3 group"
              aria-label={"Visiter " + s.nom + " (nouvelle fenêtre)"}
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#F5EFE0] group-hover:text-[#F6E27A] transition-colors truncate">
                  {s.nom}
                </span>
                <span className="block text-xs text-[#F5EFE0]/50 mt-0.5">
                  {s.role}
                  {s.actif && <span className="ml-2 text-[10px] tracking-widest uppercase text-[#F6E27A]">· vous êtes ici</span>}
                </span>
              </span>
              <ExternalLink className="h-4 w-4 shrink-0 text-[#D4AF37]" />
            </a>
          ))}
        </div>
      </div>

      <div className="bordeaux-band rounded-2xl mt-14 px-6 py-10 text-center">
        <ShieldCheck className="h-7 w-7 mx-auto text-[#F6E27A]" />
        <p className="font-display italic text-xl md:text-2xl text-[#F5EFE0]/90 max-w-2xl mx-auto mt-4 leading-relaxed">
          « {SIGNATURE} »
        </p>
        <p className="text-xs text-[#F5EFE0]/55 mt-3">
          La même promesse gravée sur chaque domaine du réseau, chaque étiquette de l&rsquo;Empire.
        </p>
      </div>
    </div>
  );
}
